/**
 * Calcula las casillas a las que se puede apuntar con una acción.
 *
 * @param {object} action - El objeto de la acción disponible.
 * @param {object} origin - Las coordenadas de origen de la acción {x, y}.
 * @param {object} game - El estado completo del juego.
 * @param {object} actingUnit - La unidad que está realizando la acción.
 * @returns {Array<{x: number, y: number}>} - Un array de coordenadas válidas.
 */
export default function calculateTargetableTiles(action, origin, game, actingUnit) {
  // 1. Validación de parámetros robusta y corregida
  if (!action || !origin || !game?.map || (action.target_type === 'move' && !actingUnit)) {
    console.warn("[calculateTargetableTiles] Parámetros inválidos.", { action, origin, game, actingUnit });
    return [];
  }

  const { width, height } = game.map;
  
  // Get units from the current snapshot if available, otherwise fall back to legacy
  let allUnits = [];
  if (game.snapshots?.current?.dynamic_objects?.units) {
    allUnits = game.snapshots.current.dynamic_objects.units;
  } else if (game.units) {
    // Legacy fallback
    allUnits = game.units;
  }
  
  const occupiedTiles = new Set(allUnits.map(u => `${u.x}-${u.y}`));

  switch (action.target_type) {
    case 'move': {
      // ✅ ¡CORRECCIÓN CLAVE! Usamos la unidad que nos pasan como parámetro.
      const moveRange = actingUnit?.movement;

      if (moveRange === undefined) {
        console.warn("La unidad actuante no tiene un rango de movimiento definido.", { actingUnit });
        return [];
      }
      
      const targetableTiles = [];
      for (let x = -moveRange; x <= moveRange; x++) {
        for (let y = -moveRange; y <= moveRange; y++) {
          if (Math.abs(x) + Math.abs(y) > moveRange) continue;

          const targetX = origin.x + x;
          const targetY = origin.y + y;

          if (targetX >= 0 && targetX < width && targetY >= 0 && targetY < height) {
            // Permite moverse a casillas desocupadas o a la propia casilla de origen
            if (!occupiedTiles.has(`${targetX}-${targetY}`) || (targetX === origin.x && targetY === origin.y)) {
               targetableTiles.push({ x: targetX, y: targetY });
            }
          }
        }
      }
      return targetableTiles;
    }

    case 'melee':
    case 'range': {
      // Esta lógica ya era correcta y no necesita cambios.
      const { range, min_range = 0, max_range } = action.properties;
      const finalRange = range ?? max_range;

      if (finalRange === undefined) {
        console.warn("La acción no tiene una propiedad 'range' o 'max_range' definida.", { action });
        return [];
      }

      const targetableTiles = [];
      for (let dx = -finalRange; dx <= finalRange; dx++) {
        for (let dy = -finalRange; dy <= finalRange; dy++) {
          const distance = Math.abs(dx) + Math.abs(dy);
          
          if (distance >= min_range && distance <= finalRange) {
            const x = origin.x + dx;
            const y = origin.y + dy;

            if (x >= 0 && x < width && y >= 0 && y < height) {
               targetableTiles.push({ x, y });
            }
          }
        }
      }
      return targetableTiles;
    }

    case 'self':
    case 'defend':
      return [origin];

    default:
      console.warn(`[calculateTargetableTiles] Target type "${action.target_type}" no implementado.`, action);
      return [];
  }
}